import React, { Component } from 'react';
import { findDOMNode } from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { v4 as getUUID } from 'uuid';
import Loader from '@adsk/bim360-matrix-react-loader';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import IconButton from '@adsk/bim360-matrix-react-icon-button';
import InfiniteScrollItemContainer from './InfiniteScrollItemContainer';
import './_InfiniteScroll.scss';
import scrollToButtonArrowSvg from '@adsk/bim360-matrix-react-assets/scroll-to-button-arrow.svg';
import Button from '@adsk/bim360-matrix-react-button';

import scrollTo from '@adsk/bim360-matrix-react-helpers/es/scrollTo';
import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';

/**
 * @class InfiniteScroll - A component used to infinite scroll on a list vertically. The scroll can be upwards on downwards.
 **/
class InfiniteScroll extends Component {
  constructor(props) {
    super(props);

    this.init(props);

    this.state = this.getInitialState();

    this._handleScrollBinded = this._handleScroll.bind(this);
    this._scrollToBottom = this._scrollToBottom.bind(this);
    this._scrollToTop = this._scrollToTop.bind(this);
    this._onItemResizeBinded = this._onItemResize.bind(this);
    this._handleContainerResizeBinded = this._handleContainerResize.bind(this);
    this._handleTryAgain = this._handleTryAgain.bind(this);
  }

  init(props) {
    this.dirty = true;

    // Empty the itemsMetaData array only if we initialize the list with new items.
    // In case we only load some new items to the existing list, don't empty it, and push them to the end of the list.
    if (!this._isLoadingMore) {
      this.itemsMetaData = [];
    }

    for (let i = this.itemsMetaData.length; i < props.items.length; i++) {
      this.itemsMetaData.push({ key: getUUID() });
    }

    this._isLoadingMore = false;
  }

  getInitialState() {
    return {
      itemsHiddenMap: Array(this.props.items.length).fill(false),
      topPlaceHolderHeight: 0,
      bottomPlaceHolderHeight: 0,
      displayScrollToButton: false,
      errorLoadingMoreItems: false
    };
  }

  /**
   * componentDidMount
   */
  componentDidMount() {
    this._container.addEventListener('scroll', this._handleScrollBinded);
    this._container.addEventListener('mouseup', this._handleScrollBinded);

    this.height = this._container.offsetHeight;

    this.resizeObserver = new ResizeObserver((entries, observer) => {
      const entry = entries[0];
      if (entry) {
        const currentHeight = entry.contentRect.height;
        if (this.height !== currentHeight) {
          this.height = currentHeight;
          this._handleContainerResizeBinded();
        }
      }
    });

    this.resizeObserver.observe(this._container);

    if (this.props.direction === InfiniteScroll.Direction.UP) {
      this._scrollToBottom();
    } else {
      this._scrollToTop();
    }

    this._prevScrollHeight = this._container.scrollHeight;

    this.hideOutOfScreenItems();
  }

  componentDidUpdate() {
    // When the scroll direction is up we'll add new items on top, because of that we'll need to adjust the scroll in
    // order to maintain the scroll position
    if (this._scrollAfterLoadingMore && this.props.direction === InfiniteScroll.Direction.UP) {
      this._adjustScrollPositionAfterLoad();
      this._scrollAfterLoadingMore = false;
    }

    if (this.dirty) {
      this.hideOutOfScreenItems();
    }
  }

  /**
   * componentWillUnmount
   */
  componentWillUnmount() {
    this._container.removeEventListener('scroll', this._handleScrollBinded);
    this._container.removeEventListener('mouseup', this._handleScrollBinded);
    this.resizeObserver.disconnect();

    this._prevScrollHeight = undefined;
    this._scrollAfterLoadingMore = undefined;
    this._isLoadingMore = undefined;
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.items.length !== nextProps.items.length) {
      this._scrollAfterLoadingMore = true;
      this.init(nextProps);
      this.setState(this.getInitialState());
    }
  }

  _onItemResize(index, height) {
    this.itemsMetaData[index].height = height;
    this.dirty = true;
    this.hideOutOfScreenItems();
  }

  onScrollChanged() {
    this.hideOutOfScreenItems();

    if (!this.scrollChangedDebounced) {
      this.scrollChangedDebounced = debounce(() => {
        this.hideOutOfScreenItems();

        if (this.props.onScrollChanged) {
          const visibleItems = this.props.items.filter((item, index) => {
            return !this.state.itemsHiddenMap[index];
          });

          this.props.onScrollChanged(visibleItems);
        }
      }, 500);
    }

    this.scrollChangedDebounced();
  }

  calculateItemsHeights() {
    // If list container is not visible, don't calculate yet.
    const itemIndex = this.getItemIndexByCurrentScroll();
    let currentItemMetaData = this.itemsMetaData[itemIndex];

    if (!currentItemMetaData.domNode) {
      currentItemMetaData.domNode = findDOMNode(currentItemMetaData.ref); // eslint-disable-line react/no-find-dom-node
    }

    // Although we assigned value to domNode, we have to make sure it's not null.
    if (!currentItemMetaData.domNode || currentItemMetaData.domNode.offsetHeight === 0) {
      return false;
    }

    // Calculate top, bottom and height for each item.
    for (let i = 0; i < this.props.items.length; i++) {
      currentItemMetaData = this.itemsMetaData[i];

      if (!currentItemMetaData.domNode) {
        currentItemMetaData.domNode = findDOMNode(currentItemMetaData.ref); // eslint-disable-line react/no-find-dom-node
      }

      const itemTop = this.itemsMetaData[i - 1] ? this.itemsMetaData[i - 1].bottom : 0;
      const itemHeight = currentItemMetaData.height
        ? currentItemMetaData.height
        : currentItemMetaData.domNode
          ? currentItemMetaData.domNode.offsetHeight
          : 0;

      currentItemMetaData.top = itemTop;
      currentItemMetaData.height = itemHeight;
      currentItemMetaData.bottom = itemHeight + itemTop;
    }

    this.dirty = false;
    return true;
  }

  getItemIndexByCurrentScroll() {
    if (this._container) {
      const scrollTop = this._container.scrollTop;

      for (let i = 0; i < this.props.items.length; i++) {
        if (this.itemsMetaData[i].top <= scrollTop && this.itemsMetaData[i].bottom >= scrollTop) {
          if (this.props.direction === InfiniteScroll.Direction.DOWN) {
            return i;
          } else {
            return this.props.items.length - i - 1;
          }
        }
      }
    }

    return 0;
  }

  hideOutOfScreenItems() {
    if (!this._container || this.props.items.length === 0) {
      return;
    }

    if (this.dirty) {
      const valid = this.calculateItemsHeights();
      if (!valid) {
        return;
      }
    }

    const scrollTop = this._container.scrollTop;
    const scrollBottom = scrollTop + this._container.offsetHeight;
    const itemsMetaData = this.itemsMetaData;
    const lastItemBounds = itemsMetaData[this.props.items.length - 1];
    const itemsHiddenMap = this.state.itemsHiddenMap;
    let topPlaceHolderHeight = this.state.topPlaceHolderHeight;
    let bottomPlaceHolderHeight = this.state.bottomPlaceHolderHeight;
    let topPlaceHolderUpdated = false;

    for (let i = 0; i < this.props.items.length; i++) {
      // Check if the item is inside the scroll view
      if (
        itemsMetaData[i].top <= scrollBottom + this.props.extraBuffer &&
        itemsMetaData[i].bottom >= scrollTop - this.props.extraBuffer
      ) {
        itemsHiddenMap[i] = false;

        if (!topPlaceHolderUpdated) {
          topPlaceHolderHeight = itemsMetaData[i].top;
          topPlaceHolderUpdated = true;
        }

        bottomPlaceHolderHeight = lastItemBounds.bottom - itemsMetaData[i].bottom;
      } else {
        itemsHiddenMap[i] = true;
      }
    }

    this.setState({
      itemsHiddenMap,
      topPlaceHolderHeight,
      bottomPlaceHolderHeight
    });
  }

  _handleContainerResize() {
    this.onScrollChanged();
    this._setScrollToButtonDisplay();
  }

  _handleScroll(event) {
    if (this._container) {
      this.onScrollChanged();
      this._setScrollToButtonDisplay();

      if (event && event.target && this.props.hasMore) {
        const distanceFromEnd = this._calculateDistanceFromEnd(this.props.direction);
        if (distanceFromEnd < this.props.loadingThreshold && !this.state.errorLoadingMoreItems) {
          this.handleLoadMore();
        }
      }
    }
  }

  handleLoadMore() {
    if (!this._isLoadingMore) {
      this._isLoadingMore = true;
      // Adding 10 to the loader height. Otherwise it doesn't scroll enough.
      const loaderHeightForScroll = this.props.loaderContainerHeight + 10;

      const promiseResult = this.props.loadMore();
      promiseResult &&
        promiseResult.catch(() => {
          let scrollPositionAfterFailure;

          if (this.props.direction === InfiniteScroll.Direction.UP) {
            if (this._container.scrollTop < this.props.loaderContainerHeight) {
              scrollPositionAfterFailure = loaderHeightForScroll;
            }
          } else {
            if (this._container.scrollTop > this.props.loaderContainerHeight) {
              scrollPositionAfterFailure = this._container.scrollHeight - loaderHeightForScroll;
            }
          }

          if (scrollPositionAfterFailure) {
            this._scrollTo(scrollPositionAfterFailure);
          }
          this._isLoadingMore = false;
          this.setState({ errorLoadingMoreItems: true });
        });
    }
  }

  /**
   * Calculates the scroll distance from the end of the container according to the scrolling direction (if the scrolling direction
   * is up then the distance is from the top)
   * @param direction - Infinite scroll direction
   * @return {number} - the scroll distance from the end
   * @private
   */
  _calculateDistanceFromEnd(direction) {
    const { scrollTop } = this._container;
    return direction === InfiniteScroll.Direction.DOWN
      ? this._container.scrollHeight - (scrollTop + this._container.clientHeight)
      : scrollTop;
  }

  /**
   * Sets in state whether to display the scroll to button
   * @param distanceFromEnd - scroll distance from the end according to the scroll direction
   * @private
   */
  _setScrollToButtonDisplay() {
    const directionForButton =
      this.props.direction === InfiniteScroll.Direction.DOWN
        ? InfiniteScroll.Direction.UP
        : InfiniteScroll.Direction.DOWN;
    const distanceFromEnd = this._calculateDistanceFromEnd(directionForButton);
    if (this.props.showScrollToButton) {
      if (distanceFromEnd > this.props.loadingThreshold) {
        if (!this.state.displayScrollToButton) {
          this.setState({ displayScrollToButton: true });
        }
      } else if (this.state.displayScrollToButton) {
        this.setState({ displayScrollToButton: false });
      }
    }
  }

  /**
   * Adjust current scroll position after more elements were loaded
   * @private
   */
  _adjustScrollPositionAfterLoad() {
    const heightDiff = this._container.scrollHeight - this._prevScrollHeight;

    if (heightDiff > 0) {
      const scrollFix = this._container.scrollTop + heightDiff;

      this._prevScrollHeight = this._container.scrollHeight;
      this._scrollTo(scrollFix);
    }
  }

  /**
   * Scrolls the container to a given location
   * @param position - The position to scroll to
   * @param [duration] - The duration of the scroll
   * @private
   */
  _scrollTo(position, duration) {
    const difference = Math.abs(position - this._container.scrollTop);
    const perTick = difference / (60 * duration);

    // If the number of pixels that the scroll moves per frame is larger than the extraBuffer,
    // then animating the scroll will result in blank frames - So just jump to the position.
    if (perTick > this.props.extraBuffer) {
      duration = 0;
    }

    scrollTo(this._container, position, duration);
  }

  /**
   * Scrolls to the bottom of the container
   * @param [duration] - The duration of the scroll
   * @private
   */
  _scrollToBottom(duration) {
    this._scrollTo(this._container.scrollHeight, duration);
  }

  /**
   * Scrolls to the top of the container
   * @param [duration] - The duration of the scroll
   * @private
   */
  _scrollToTop(duration) {
    this._scrollTo(0, duration);
  }

  _handleTryAgain() {
    this.setState({ errorLoadingMoreItems: false });
    this.handleLoadMore();
  }

  _renderLoader(direction) {
    return !this.state.errorLoadingMoreItems &&
      this.props.hasMore &&
      this.props.direction === direction ? (
        <div style={{ height: `${this.props.loaderContainerHeight}px` }}>
          <Loader />
        </div>
      ) : null;
  }

  _renderTryAgainButton() {
    if (
      this.state.errorLoadingMoreItems &&
      (this.props.errorLoadingMoreItemsText || this.props.tryAgainButtonText)
    ) {
      return (
        <div className="InfiniteScroll__error-loading-more">
          {this.props.errorLoadingMoreItemsText && (
            <span className="InfiniteScroll__error-loading-more-text">
              {this.props.errorLoadingMoreItemsText}
            </span>
          )}
          {this.props.tryAgainButtonText && (
            <Button styleType={Button.StyleType.INLINE_LINK} onClick={this._handleTryAgain}>
              {this.props.tryAgainButtonText}
            </Button>
          )}
        </div>
      );
    }
    return null;
  }

  _renderScrollToButton(direction) {
    const isScrollToBottom = direction === InfiniteScroll.Direction.UP;
    const scrollToHandler = isScrollToBottom ? this._scrollToBottom : this._scrollToTop;
    const shouldDisplayButton =
      !this.props.disabled && this.props.showScrollToButton && this.state.displayScrollToButton;
    const cx = classNames('InfiniteScroll__scroll-to-button', {
      'InfiniteScroll__scroll-to-button--visible': shouldDisplayButton,
      'InfiniteScroll__scroll-to-button--bottom': isScrollToBottom,
      'InfiniteScroll__scroll-to-button--top': !isScrollToBottom
    });

    return this.props.direction === direction ? (
      <IconButton className={cx} onClick={() => scrollToHandler(0.5)}>
        <SvgIcon
          className="InfiniteScroll__scroll-to-button-arrow"
          transform={isScrollToBottom ? 'rotate(180 8 9)' : null}
        >
          {scrollToButtonArrowSvg}
        </SvgIcon>
      </IconButton>
    ) : null;
  }

  saveRef(index, ref) {
    // Verify that the wanted index exists inside the items array to prevent overflow.
    // Index can be greater the items.length in case that until the callback has been triggered, the list has been changed with smaller number of items.
    if (index < this.props.items.length) {
      this.itemsMetaData[index].ref = ref;
    }
  }

  renderItems(items) {
    return items.map((item, i) => {
      if (!this.itemsMetaData[i]) return null;
      return (
        <InfiniteScrollItemContainer
          key={`item-${this.itemsMetaData[i].key}`}
          hidden={this.state.itemsHiddenMap[i]}
          itemIndex={i}
          item={item}
          onItemResize={this._onItemResizeBinded}
          ref={ref => {
            this.saveRef(i, ref);
          }}
        />
      );
    });
  }

  render() {
    const cx = classNames(
      'InfiniteScroll',
      {
        'InfiniteScroll--disabled': this.props.disabled
      },
      this.props.className
    );

    return (
      <div className="InfiniteScroll__outer-container">
        {this._renderScrollToButton(InfiniteScroll.Direction.DOWN)}
        <div
          className={cx}
          style={this.props.style}
          ref={_ => {
            this._container = _;
          }}
        >
          <div>
            <div
              className="InfiniteScroll__place-holder"
              style={{ height: `${this.state.topPlaceHolderHeight}px` }}
              ref={ref => (this.topPlaceHolder = ref)}
            />
            {this._renderLoader(InfiniteScroll.Direction.UP)}
            {this.renderItems(this.props.items)}
            {this._renderLoader(InfiniteScroll.Direction.DOWN)}
            {this._renderTryAgainButton()}
            <div
              className="InfiniteScroll__place-holder"
              style={{
                height: `${this.state.bottomPlaceHolderHeight}px`
              }}
              ref={ref => (this.bottomPlaceHolder = ref)}
            />
          </div>
        </div>
        {this._renderScrollToButton(InfiniteScroll.Direction.UP)}
      </div>
    );
  }
}

InfiniteScroll.Direction = {
  UP: 'UP',
  DOWN: 'DOWN'
};

InfiniteScroll.defaultProps = {
  items: [],
  direction: InfiniteScroll.Direction.DOWN,
  showScrollToButton: true,
  loadingThreshold: 200,
  loaderContainerHeight: 50,
  extraBuffer: 2000
};

InfiniteScroll.propTypes = {
  /**
   * Boolean for disabling the component
   */
  disabled: PropTypes.bool,
  /**
   * Whether there are more items to load
   */
  hasMore: PropTypes.bool,
  /**
   * A callback for loading more items when reaching scroll position
   */
  loadMore: PropTypes.func,
  /**
   * The items of the list
   */
  items: PropTypes.array,
  /**
   * The scroll vertical direction for loading more items
   */
  direction: PropTypes.oneOf(objectValues(InfiniteScroll.Direction)),
  /**
   * Whether to show the button which scrolls all the way up or down (depends on the direction)
   */
  showScrollToButton: PropTypes.bool,
  /**
   * A class name for the container
   */
  className: PropTypes.string,
  /**
   * Style for the container
   */
  style: PropTypes.object,
  /**
   * The scroll distance to trigger loading more items
   */
  loadingThreshold: PropTypes.number,
  /**
   * Height for loader container in pixels
   */
  loaderContainerHeight: PropTypes.number,
  /**
   * Number of extra pixels to render above and bellow the current view.
   */
  extraBuffer: PropTypes.number,
  /**
   * Optional callback to be called when the current frame has been changed.
   * The callback has an 'items' argument, that includes all the visible items.
   */
  onScrollChanged: PropTypes.func,
  /**
   * Optional text to display when error loading more items occurred.
   */
  errorLoadingMoreItemsText: PropTypes.string,
  /**
   * Optional button text to display when error loading more items occurred.
   * Clicking the button will try to load more items again.
   */
  tryAgainButtonText: PropTypes.string
};

export default InfiniteScroll;
